import { useState, useCallback, useRef } from 'react';
import { useVoxtralTTS, getVoxtralVoiceForPersona, VoxtralVoiceId } from './useVoxtralTTS';
import { useKittenTTS, getKittenVoiceForPersona, KittenVoiceId } from './useKittenTTS';
import { useInworldTTS, getVoiceForPersona, InworldVoiceId } from './useInworldTTS';
import { useSelectedVoice } from '../components/AiVoiceControls';

// Cloudflare Workers AI TTS voices (Deepgram Aura-1) — free, no API key needed
const CF_VOICES = [
  'angus', 'asteria', 'arcas', 'orion', 'orpheus', 'athena',
  'luna', 'zeus', 'perseus', 'helios', 'hera', 'stella',
] as const;
type CfVoiceId = (typeof CF_VOICES)[number];

// Map persona voice hints to Cloudflare voices
const CF_PERSONA_MAP: Record<string, CfVoiceId> = {
  Dennis: 'zeus',
  Sarah: 'asteria',
  James: 'orion',
  Emma: 'luna',
  Michael: 'perseus',
  Olivia: 'athena',
  David: 'arcas',
  Sophie: 'stella',
  Rafael: 'orpheus',
  Omar: 'helios',
  Clive: 'angus',
  Luna: 'luna',
  Ashley: 'hera',
  Deborah: 'athena',
  Wendy: 'stella',
  Timothy: 'perseus',
  jessica: 'asteria',
  emma: 'luna',
  james: 'orion',
  michael: 'perseus',
  sophia: 'athena',
  oliver: 'arcas',
  aria: 'stella',
  daniel: 'zeus',
  Jasper: 'orion',
  Luna2: 'hera',
};

function getCfVoiceForPersona(voiceHint?: string): CfVoiceId {
  if (!voiceHint) return 'asteria';
  return CF_PERSONA_MAP[voiceHint] || 'asteria';
}

/**
 * Cloudflare Workers AI TTS — uses Deepgram Aura-1 via your AI binding.
 * Free, no API key needed, natural voices.
 */
function useCloudflareTTS() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text: string, options: { voice?: CfVoiceId; speed?: number } = {}) => {
      stop();
      if (!text.trim()) return;
      setError(null);
      setIsPlaying(true);

      try {
        abortRef.current = new AbortController();
        const res = await fetch('/_api/tts/cloudflare', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text, speaker: options.voice || 'asteria' }),
          signal: abortRef.current.signal,
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          throw new Error(`Cloudflare TTS ${res.status}: ${errText.slice(0, 100)}`);
        }

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;

        setIsSpeaking(true);
        await new Promise<void>((resolve, reject) => {
          audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Audio playback failed')); };
          audio.play().catch(reject);
        });
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError(err?.message || 'Cloudflare TTS failed');
        throw err;
      } finally {
        setIsPlaying(false);
        setIsSpeaking(false);
        abortRef.current = null;
      }
    },
    [stop],
  );

  return { speak, stop, isPlaying, isSpeaking, error };
}

/**
 * Unified TTS hook — tries Cloudflare Workers AI first (free, natural voices),
 * then Voxtral, Kitten, and Inworld as fallbacks.
 */
export function useTTS() {
  const cf = useCloudflareTTS();
  const voxtral = useVoxtralTTS();
  const kitten = useKittenTTS();
  const inworld = useInworldTTS();
  const useCfRef = useRef(true);
  const useVoxtralRef = useRef(true);
  const useKittenRef = useRef(true);
  const selectedVoice = useSelectedVoice();

  const speak = useCallback(
    async (text: string, options: { voiceId?: InworldVoiceId; speakingRate?: number } = {}) => {
      // --- Cloudflare Workers AI (first choice — free, natural Deepgram Aura voices) ---
      if (useCfRef.current) {
        const cfVoice: CfVoiceId = options.voiceId
          ? getCfVoiceForPersona(options.voiceId)
          : selectedVoice !== 'auto'
            ? (CF_VOICES.includes(selectedVoice as CfVoiceId) ? selectedVoice as CfVoiceId : 'asteria')
            : 'asteria';

        try {
          await cf.speak(text, { voice: cfVoice, speed: options.speakingRate });
          if (!cf.error) return;
        } catch {
          // Cloudflare failed — fall through
        }

        if (cf.error?.includes('503')) {
          useCfRef.current = false;
        }
      }

      // --- Voxtral (second choice) ---
      if (useVoxtralRef.current) {
        const voxtralVoice: VoxtralVoiceId = options.voiceId
          ? getVoxtralVoiceForPersona(options.voiceId)
          : selectedVoice !== 'auto'
            ? (selectedVoice as VoxtralVoiceId)
            : 'jessica';

        try {
          await voxtral.speak(text, { voice: voxtralVoice, speed: options.speakingRate });
          if (!voxtral.error) return;
        } catch {
          // Voxtral failed — fall through
        }

        if (voxtral.error?.includes('503')) {
          useVoxtralRef.current = false;
        }
      }

      // --- Kitten (third choice) ---
      if (useKittenRef.current) {
        const kittenVoice: KittenVoiceId = options.voiceId
          ? getKittenVoiceForPersona(options.voiceId)
          : selectedVoice !== 'auto'
            ? (selectedVoice as KittenVoiceId)
            : 'Jasper';

        try {
          await kitten.speak(text, { voice: kittenVoice, speed: options.speakingRate });
          if (!kitten.error) return;
        } catch {
          // Kitten failed — fall through
        }

        if (kitten.error?.includes('503')) {
          useKittenRef.current = false;
        }
      }

      // --- Inworld (last resort) ---
      await inworld.speak(text, options);
    },
    [cf, voxtral, kitten, inworld, selectedVoice],
  );

  const stop = useCallback(() => {
    cf.stop();
    voxtral.stop();
    kitten.stop();
    inworld.stop();
  }, [cf, voxtral, kitten, inworld]);

  return {
    speak,
    stop,
    isPlaying: cf.isPlaying || voxtral.isPlaying || kitten.isPlaying || inworld.isPlaying,
    isSpeaking: cf.isSpeaking || voxtral.isSpeaking || kitten.isSpeaking || inworld.isSpeaking,
    error: cf.error && voxtral.error && kitten.error && inworld.error ? inworld.error : null,
  };
}

/**
 * Get a voice ID for a persona — works with all providers
 */
export { getVoiceForPersona, getKittenVoiceForPersona, getVoxtralVoiceForPersona, getCfVoiceForPersona };
